import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getClinicianCaseloads } from "../lib/api";
import { printPage } from "../lib/print";

const PrintIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
    <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect width="12" height="8" x="6" y="14" />
  </svg>
);

export default function PrintableClinicianCaseloads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getClinicianCaseloads();
      setData(res.data);
    } catch {
      toast.error("Failed to load clinician caseloads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const totalAssigned = data ? data.clinicians.reduce((sum, c) => sum + c.students.length, 0) : 0;

  const handlePrint = () => {
    if (!data) return;

    const studentRow = (s) =>
      `<li style="font-size: 15px; line-height: 1.9;">${s.display_name}${s.grade ? ` <span style="color:#666;">— Grade ${s.grade}</span>` : ""}</li>`;

    const clinicianBlock = (c) =>
      `<div class="caseload-block" style="break-inside: avoid; margin-bottom: 20px;">
        <h3 style="font-size: 18px; font-weight: bold; color: #00305E; border-bottom: 2px solid #00305E; padding-bottom: 4px; margin-bottom: 8px;">
          ${c.name} <span style="font-weight: normal; font-size: 13px; color: #666;">(${c.students.length} student${c.students.length === 1 ? "" : "s"})</span>
        </h3>
        ${c.students.length
          ? `<ol style="margin: 0; padding-left: 26px;">${c.students.map(studentRow).join("")}</ol>`
          : `<p style="color:#999; font-style: italic; margin: 0;">No students assigned</p>`}
      </div>`;

    let content = data.clinicians.map(clinicianBlock).join("");

    content += `<div class="caseload-block" style="break-inside: avoid; margin-top: 28px;">
      <h3 style="font-size: 18px; font-weight: bold; color: #b45309; border-bottom: 2px solid #b45309; padding-bottom: 4px; margin-bottom: 8px;">
        Unassigned &mdash; No Clinician <span style="font-weight: normal; font-size: 13px; color: #666;">(${data.unassigned.length} student${data.unassigned.length === 1 ? "" : "s"})</span>
      </h3>
      ${data.unassigned.length
        ? `<ol style="margin: 0; padding-left: 26px;">${data.unassigned.map(studentRow).join("")}</ol>`
        : `<p style="color:#999; font-style: italic; margin: 0;">All students are assigned to a clinician</p>`}
    </div>`;

    printPage({
      title: "Clinician Caseloads",
      content,
      extraStyles: `@page { margin: 1.2cm; } .caseload-block { break-inside: avoid; }`,
    });
  };

  const StudentList = ({ students, empty }) => (
    students.length ? (
      <ol className="list-decimal pl-6 space-y-0.5">
        {students.map((s) => (
          <li key={s.id} className="text-sm" data-testid={`caseload-student-${s.id}`}>
            {s.display_name}
            {s.grade && <span className="text-muted-foreground"> — Grade {s.grade}</span>}
          </li>
        ))}
      </ol>
    ) : (
      <p className="text-sm text-muted-foreground italic">{empty}</p>
    )
  );

  return (
    <Card data-testid="printable-clinician-caseloads">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Clinician Caseloads</CardTitle>
            <CardDescription>Each clinician and their assigned students, plus students with no clinician</CardDescription>
          </div>
          <Button
            onClick={handlePrint}
            disabled={!data || loading}
            className="bg-[#00305E] hover:bg-[#00305E]/90"
            data-testid="print-clinician-caseloads-btn"
          >
            <PrintIcon />Print
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]"></div>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <p className="text-xs text-muted-foreground">
              {data.clinicians.length} clinician{data.clinicians.length === 1 ? "" : "s"} · {totalAssigned} assigned · {data.unassigned.length} unassigned
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              {data.clinicians.map((c) => (
                <div key={c.name} className="border rounded-lg p-4 bg-white" data-testid={`caseload-clinician-${c.name}`}>
                  <h3 className="text-base font-bold text-[#00305E] border-b-2 border-[#00305E] pb-1 mb-2">
                    {c.name} <span className="font-normal text-xs text-muted-foreground">({c.students.length})</span>
                  </h3>
                  <StudentList students={c.students} empty="No students assigned" />
                </div>
              ))}
            </div>
            <div className="border rounded-lg p-4 bg-amber-50/50 border-amber-200" data-testid="caseload-unassigned">
              <h3 className="text-base font-bold text-amber-700 border-b-2 border-amber-300 pb-1 mb-2">
                Unassigned — No Clinician <span className="font-normal text-xs text-muted-foreground">({data.unassigned.length})</span>
              </h3>
              <StudentList students={data.unassigned} empty="All students are assigned to a clinician" />
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No caseload data available</p>
        )}
      </CardContent>
    </Card>
  );
}
